import { useState } from "react";

import { BdsIconPencilSimple, BdsText } from "@buildertrend/components";

import { friendlyDefaultDisplayValue } from "utilities/helpers";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { BTExternalLink } from "commonComponents/btWrappers/BTExternalLink/BTExternalLink";
import { BTTextArea } from "commonComponents/btWrappers/BTTextArea/BTTextArea";

import "./LinkFieldEditor.less";

export function normalizeUrl(value: string): string {
    return value.startsWith("http") ? value : `https://${value}`;
}

interface ILinkFieldEditorProps {
    value: string | null;
    customFieldId: number;
    onChange: (newValue: string | null) => void;
    onBlur: () => void;
    startInEditMode?: boolean;
}

export function LinkFieldEditor({
    value,
    customFieldId,
    onChange,
    onBlur,
    startInEditMode = false,
}: Readonly<ILinkFieldEditorProps>) {
    const [isEditing, setIsEditing] = useState(startInEditMode);

    if (!value || isEditing) {
        return (
            <BTTextArea
                id={`linkCustomFieldEditor-${customFieldId}`}
                data-testid="linkCustomFieldEditor"
                value={value ?? ""}
                readOnly={false}
                onChange={(_id, val) => onChange(val)}
                onBlur={(_id, _touched, blurValue) => {
                    if (blurValue !== undefined) {
                        onChange(blurValue);
                    }
                    setIsEditing(false);
                    onBlur();
                }}
                onFocus={() => setIsEditing(true)}
                placeholder={friendlyDefaultDisplayValue}
                formMode="click-to-edit"
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        e.preventDefault();
                        onChange((e.target as HTMLTextAreaElement).value);
                        (e.target as HTMLTextAreaElement).blur();
                    }
                }}
                blurOnChange={false}
                autoResize="height"
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus={isEditing}
            />
        );
    }

    return (
        <div className="LinkFieldEditor__display">
            <BTExternalLink
                href={normalizeUrl(value)}
                data-testid="linkCustomFieldLink"
                className="LinkFieldEditor__link"
            >
                <BdsText size="normal-md" text={value} />
            </BTExternalLink>
            <BTButton
                data-testid="editLinkButton"
                type="tertiary"
                className="LinkFieldEditor__editButton"
                onClick={(e) => {
                    e.stopPropagation();
                    setIsEditing(true);
                }}
                icon={<BdsIconPencilSimple />}
                aria-label="Edit link"
            />
        </div>
    );
}
